import { ContentState, convertFromRaw, convertToRaw, type EditorState, type RawDraftContentState } from 'draft-js';
import { isEmpty, isNil } from 'lodash';
import type { MutableRefObject } from 'react';
import React, { useCallback, useMemo } from 'react';

import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';
import type { TextAlignment } from '../../../../common/table/CellTypeConstants';
import type { CellData, DraftCellEditingSession } from '../../../../common/table/TableTypes';

import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';

import { getLocalisedContent } from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { getTableCellEditorId } from '../utils/tableEditorSessionUtils';
import type { MNHotInstance } from '../tableHotInstanceTypes';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';

type Props = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellData: CellData | undefined;
    cellEditingSession: DraftCellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    locale: string;
    textAlignment: TextAlignment | null;
    recomputeEditingTextAlignment: (editCellValue: string) => void;
};

const DraftCellEditorComponent: React.FC<Props> = ({
    element,
    elementId,
    isEditable,
    cellData,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    cellEditorRef,
    locale,
    textAlignment,
    recomputeEditingTextAlignment,
}) => {
    const textContent = useMemo((): RawDraftContentState | string | null | undefined => {
        // Only update content when editing starts, otherwise Draft can retain stale state between cells.
        if (!cellEditingSession) return;
        if (!cellData) return null;

        let contentState;

        if (!isNil(cellEditingSession.overwriteText)) {
            contentState = ContentState.createFromText(cellEditingSession.overwriteText);
        } else if (cellData.draftContent && !isFormula(cellData.value)) {
            contentState = convertFromRaw(cellData.draftContent);
        } else {
            if (isFormula(cellData.value)) return cellData.value;

            const localisedContent = getLocalisedContent(cellData, locale);
            contentState = ContentState.createFromText(localisedContent.toString());
        }

        if (!isEmpty(cellData.textStyle)) {
            contentState = applyInlineStylesThroughoutContent(contentState, cellData.textStyle);
        }

        return convertToRaw(contentState);
    }, [cellEditingSession, locale]);

    const onChange = useCallback(
        ({ editorState }: { editorState: EditorState }) => {
            if (!cellEditingSession) return;

            const currentContent = editorState.getCurrentContent();
            const editCellValue = currentContent.getPlainText();

            recomputeEditingTextAlignment(editCellValue);

            // @ts-ignore - Custom Milanote hook
            hotTableInstanceRef.current?.runHooks('onMilanoteCellEditorChange', editorState);
        },
        [cellEditingSession, hotTableInstanceRef, recomputeEditingTextAlignment],
    );

    return (
        <TableCellEditor
            editorRef={cellEditorRef}
            className="handsontable-input"
            element={element}
            isEditing={!!cellEditingSession}
            isEditable={isEditable}
            textContent={textContent}
            defaultStyleOverride={cellData?.textStyle?.length ? cellData.textStyle : undefined}
            editorId={getTableCellEditorId(elementId)}
            editorKey={TABLE_CELL_EDITOR_KEY}
            currentEditorId={currentlyEditingEditorId}
            onChange={onChange}
            // @ts-ignore - This prop appears unused. But this component is being rebuilt
            // soon, so will leave as-is for now to avoid unintended consequences.
            textAlignment={textAlignment}
        />
    );
};

export default DraftCellEditorComponent;
